// src/js/workers/facePoseWorker.js

// Use an OffscreenCanvas worker to handle all rendering tasks.

const faceSmoothFactor = 0.2; // Adjust between 0 and 1 for smoother results
const poseSmoothFactor = 0.2; // Adjust between 0 and 1 for smoother results
let smoothedFaceLandmarks = null;
let smoothedPoseLandmarks = null;
let smoothedBlendShapes = null;
let refine = true;

onmessage = (event) => {
    try {
        if (event.data.type !== 'PROCESS_FRAME') {
            console.warn("Unexpected message type received: ", event.data.type);
            return;
        }

        const { faceLandmarks, poseLandmarks, blendShapes } = event.data['results'];

        // TODO: Handle missing data gracefully--- the system should not crash if one of the data is missing!!!!
        if (!faceLandmarks || !Array.isArray(faceLandmarks)) {
            console.warn("Invalid or missing face landmarks data.");
            return;
        }

        if (!poseLandmarks || !Array.isArray(poseLandmarks)) {
            console.warn("Invalid or missing pose landmarks data.");
            return;
        }

        if (!blendShapes || typeof blendShapes !== 'object') {
            console.warn("Invalid or missing blend shapes data.");
            return;
        }

        // Refine the landmarks if needed
        let selF = null;
        let selP = null;
        let selB = null;

        if (typeof refine !== 'undefined' && refine) {
            selF = refineFaceLandmarks(faceLandmarks);
            selP = refinePoseLandmarks(poseLandmarks);
            selB = refineBlendShapes(blendShapes);
        } else {
            selF = faceLandmarks;
            selP = poseLandmarks;
            selB = blendShapes;
        }

        // Apply smoothing
        smoothedFaceLandmarks = applyEMASmoothingToFaceLandmarks(selF, smoothedFaceLandmarks, faceSmoothFactor);
        smoothedPoseLandmarks = applyEMASmoothingToPoseLandmarks(selP, smoothedPoseLandmarks, poseSmoothFactor);
        smoothedBlendShapes = applyEMASmoothingToBlendShapes(selB, smoothedBlendShapes, faceSmoothFactor);

        // console.log("Processed frame with face landmarks: ", smoothedFaceLandmarks);
        // console.log("Processed frame with pose landmarks: ", smoothedPoseLandmarks);

        // Post the processed results back to the main thread
        postMessage({
            type: 'PROCESSED_RESULTS',
            data: {
                faceLandmarks: smoothedFaceLandmarks,
                poseLandmarks: smoothedPoseLandmarks,
                blendShapes: smoothedBlendShapes
            }
        });
    } catch (error) {
        console.error("Error processing frame: ", error);
    }
};

// Function to refine face landmarks by selecting certain areas (83 * 3)
function refineFaceLandmarks(faceLandmarks) {
    if (!Array.isArray(faceLandmarks)) {
        console.warn("refineFaceLandmarks received invalid data.");
        return [];
    }

    try {
        // 'face_oval': [152, 176, 150, 172, 132, 234, 162, 54, 67, 10, 297, 284, 389, 454, 401, 435, 397, 379, 400],
        const refinedFsIndexes_r = { //handpicked parts
            'forehead': [9, 108, 337, 10],
            'face_oval': [152, 172, 132, 234, 21, 67, 297, 251, 454, 361, 397],
            'cheekbone_left': [280],
            'cheekbone_right': [50],
            'right_iris': [469, 470, 471, 472],
            'right_iris_mid': [468],
            'right_eye_outer_corner': [33],
            'right_eye_inner_corner': [133],
            'right_eye_lower_lid': [145, 144],
            'right_eye_upper_lid': [159, 160],
            'right_eyebrow': [70, 63, 105, 66, 107],
            'left_iris': [474, 475, 476, 477],
            'left_iris_mid': [473],
            'left_eye_outer_corner': [362],
            'left_eye_inner_corner': [263],
            'left_eye_lower_lid': [374, 380],
            'left_eye_upper_lid': [386, 385],
            'left_eyebrow': [300, 293, 334, 296, 336],
            'nose': [6, 195, 4, 1, 237, 457],
            'nose_left': [371, 423, 266],
            'nose_right': [203, 36, 142],
            'lip_top': [0, 37, 39, 267, 269, 61],
            'lip_bottom': [17, 14, 84, 314, 87, 317, 321, 291, 91],
            'mouth': [425, 427, 434, 205, 207, 214],
        };

        let refinedLandmarks = [];
        for (let [part, indexes] of Object.entries(refinedFsIndexes_r)) {
            indexes.forEach((index, idx) => {
                if (faceLandmarks[index]) {
                    refinedLandmarks.push({
                        ...faceLandmarks[index],
                        part: `${part}_${idx + 1}`
                    });
                }
            });
        }

        return refinedLandmarks;
    } catch (error) {
        console.error("Error refining face landmarks: ", error);
        return [];
    }
}

// Function to refine pose landmarks by selecting specific indices (6 * 3)
function refinePoseLandmarks(poseLandmarks) {
    if (!Array.isArray(poseLandmarks)) {
        console.warn("Invalid pose landmarks data. Expected an array.");
        return [];
    }

    const labeledLandmarks = [];

    try {
        const refinedIndexes = {
            'left_shoulder': 11,
            'right_shoulder': 12,
            'left_elbow': 13,
            'right_elbow': 14,
            'left_wrist': 15,
            'right_wrist': 16
        };
        
        for (const [part, index] of Object.entries(refinedIndexes)) {
            if (index < 0 || index >= poseLandmarks.length) {
                console.warn(`Index out of bounds for pose landmark '${part}': ${index}`);
                continue; // Skip out-of-bounds index
            }

            const landmark = poseLandmarks[index];
            if (landmark && typeof landmark === 'object') {
                labeledLandmarks.push({ ...landmark, part });
            } else {
                console.warn(`Invalid landmark at index ${index}. Expected an object.`);
            }
        }
    } catch (error) {
        console.error("Error labeling pose landmarks: ", error);
    }

    return labeledLandmarks;
}




// Function to refine blend shapes (15 * 1)
function refineBlendShapes(blendShapes) {
    if (!blendShapes || !blendShapes.categories || !Array.isArray(blendShapes.categories)) {
        console.warn("refineBlendShapes received invalid data.");
        return {};
    }

    try {
        const selectedBlendShapes = [
            "browOuterUpLeft", "browOuterUpRight",
            "eyeBlinkLeft", "eyeBlinkRight", 
            "eyeLookDownLeft", "eyeLookDownRight",
            "eyeLookInLeft", "eyeLookInRight",
            "eyeSqintLeft", "eyeSqintRight",
            "mouthDimpleLeft", "mouthDimpleRight", "mouthShrugUpper",
            "mouthSmileLeft", "mouthSmileRight",
            "jawOpen"
        ];

        const refinedBlendShapes = {};
        blendShapes.categories.forEach(blendShape => {
            if (selectedBlendShapes.includes(blendShape.categoryName)) {
                refinedBlendShapes[blendShape.categoryName] = blendShape.score;
            }
        });

        return refinedBlendShapes;
    } catch (error) {
        console.error("Error refining blend shapes: ", error);
        return {};
    }
}


// Function to apply EMA smoothing to face landmarks
function applyEMASmoothingToFaceLandmarks(currentLandmarks, previousLandmarks, smoothingFactor) {
    if (!Array.isArray(currentLandmarks)) {
        console.warn("Invalid current landmarks data. Expected an array.");
        return [];
    }
    if (!previousLandmarks) {
        return currentLandmarks;
    }

    try {
        return currentLandmarks.map((current, index) => {
            const previous = previousLandmarks[index];

            // Check if the current or previous landmarks are undefined or invalid
            if (!current || !previous || typeof current.x !== 'number' || typeof previous.x !== 'number') {
                console.warn(`Invalid or missing landmarks at index ${index}. Skipping smoothing.`);
                return current; // Return the current value as it is if invalid
            }

            return {
                x: isNaN(previous.x) ? current.x : smoothingFactor * previous.x + (1 - smoothingFactor) * current.x,
                y: isNaN(previous.y) ? current.y : smoothingFactor * previous.y + (1 - smoothingFactor) * current.y,
                z: isNaN(previous.z) ? current.z : smoothingFactor * previous.z + (1 - smoothingFactor) * current.z,
                part: current.part // Preserve the 'part' key for labeled landmarks
            };
        });
    } catch (error) {
        console.error("Error applying EMA smoothing to face landmarks: ", error);
        return currentLandmarks;
    }
}


// Function to apply EMA smoothing to pose landmarks
function applyEMASmoothingToPoseLandmarks(currentLandmarks, previousLandmarks, smoothingFactor) {
    if (!Array.isArray(currentLandmarks)) {
        console.warn("Invalid current landmarks data. Expected an array.");
        return [];
    }
    if (!previousLandmarks) {
        return currentLandmarks;
    }

    try {
        return currentLandmarks.map((current, index) => {
            const previous = previousLandmarks[index];

            // Check if the current or previous landmarks are undefined or invalid
            if (!current || !previous || typeof current.x !== 'number' || typeof previous.x !== 'number') {
                console.warn(`Invalid or missing landmarks at index ${index}. Skipping smoothing.`);
                return current; // Return the current value as it is if invalid
            }

            return {
                x: isNaN(previous.x) ? current.x : smoothingFactor * previous.x + (1 - smoothingFactor) * current.x,
                y: isNaN(previous.y) ? current.y : smoothingFactor * previous.y + (1 - smoothingFactor) * current.y,
                z: isNaN(previous.z) ? current.z : smoothingFactor * previous.z + (1 - smoothingFactor) * current.z,
                part: current.part // Preserve the 'part' key for labeled landmarks
            };
        });
    } catch (error) {
        console.error("Error applying EMA smoothing to face landmarks: ", error);
        return currentLandmarks;
    }
}

// Function to apply EMA smoothing to blend shapes
function applyEMASmoothingToBlendShapes(currentBlendShapes, previousBlendShapes, smoothingFactor) {
    if (!previousBlendShapes) {
        return { ...currentBlendShapes };
    }

    try {
        let smoothedBlendShapes = {};
        for (const key in currentBlendShapes) {
            if (currentBlendShapes.hasOwnProperty(key)) {
                const currentValue = currentBlendShapes[key];
                const previousValue = previousBlendShapes[key];

                if (isNaN(currentValue)) {
                    smoothedBlendShapes[key] = currentValue;
                } else if (previousValue === undefined || isNaN(previousValue)) {
                    smoothedBlendShapes[key] = currentValue;
                } else {
                    smoothedBlendShapes[key] = smoothingFactor * previousValue + (1 - smoothingFactor) * currentValue;
                }
            }
        }
        return smoothedBlendShapes;
    } catch (error) {
        console.error("Error applying EMA smoothing to blend shapes: ", error);
        return currentBlendShapes;
    }
}





//boredom related stuff
// Inner brow raise
// Outer brow raise
// Inner and outer brow raised together
// Dimpler
// Brow lowerer
// Lid tightener
// Brow lowered with tightened lids
// Tightened lids will lead to a lowered brow
// Lip corner puller
// Eye closure

